import moment from "moment";

export const policyInitialValues = {
  company: "",
  // Product Selection — which of OD / TP this shared data entry should
  // generate, and which company underwrites each. Both always on: every
  // policy generates both OD and TP. The company for each is hidden and
  // auto-derived from the selected Make & Model master (see
  // BasicPolicyDetailsStep's handleMakeModelChange) — not chosen here.
  odSelected: true,
  tpSelected: true,
  odCompany: "",
  tpCompany: "",
  // Which company bills PA Cover when it's routed "Through: Company" and OD
  // and TP have different companies. Derived from `settings.PACompany` — see
  // companyRules.settingsToLegacyFields.
  paCoverInsurerCompany: "",
  // Canonical company routing. ODCompany / TPCompany follow the selected Make
  // & Model master; PACompany is the "PA Cover Company" dropdown (Brisk by
  // default, matching paCoverCompany's default below). Every other company
  // field on this form is derived from it.
  settings: {
    isMultipleCompany: false,
    PACompany: "Brisk",
    ODCompany: "",
    TPCompany: "",
  },
  makeModel: "",
  // "single": one Make & Model row, OD and TP are that row's company.
  // "multiple": a Combined Make & Model record (`makeModelCombo`) — an OD
  // company's row and a TP company's row, a vehicle each. `makeModel` is then the
  // OD row. `_legacyMultiple` marks a SAVED policy that has two companies but no
  // record (sold before records existed): it keeps what it was sold with.
  companyChoice: "single",
  // What the policy covers when ONE company sells it: "both" (OD + TP, as always), "od" (own damage only) or "tp"
  // (third party only). Needs the server's POLICY_COVER_CHOICE_ENABLED (GET /onlinePolicy-config `coverChoice`).
  // Multiple company is always "both". See companyRules.coverageOf.
  coverage: "both",
  makeModelCombo: "",
  _legacyMultiple: false,
  vehicleVariant: "",
  rtoCityLocation: "",
  // "A" or "B" — taken from the selected RTO City/Location's zone, or asked
  // for when that RTO has none. Drives the premium's base rate.
  zone: "",
  RTORegion: "",
  RTOCity: "",
  idv: "",
  // Auto-filled from the Make & Model master when that vehicle is not in the
  // insurer's list; the portal then derives the IDV from the selling price.
  modelNotFound: false,
  manufacturerSellingPrice: "",
  idvRangeFrom: "",
  idvRangeTo: "",
  discount: "",
  manufacturingYear: moment().format("YYYY") || "",
  manufacturingMonth: moment().format("MM") || "",
  purchaseDate: new Date() || "",
  registrationDate: new Date() || "",
  zeroDepreciation: true,
  // Filled from the selected Make & Model's Nil Dip Price; when that row has
  // none, the user is asked and the value is saved back to the master.
  zeroDepreciationPercentage: "",
  // UI-only: true when the value came from the Make & Model master, so the form
  // knows not to write it back. Neither is sent to the backend.
  _nilDipFromMaster: false,
  // Brisk is the default PA company; the Make & Model master can raise its
  // ₹177 minimum. The form updates this when another PA company is selected.
  _paCoverMinAmount: 177,
  // Forced off for vehicles whose master row does not switch TPPD on.
  tppdRestrict: true,
  paCover: false,
  // Brisk by default: only "Brisk" triggers CPA/RSA certificate creation, so
  // defaulting to "Company" silently stopped certificates being made.
  paCoverCompany: "Brisk",
  paCoverAmount: null,
  nomineeName: "",
  nomineeGender: "",
  nomineeRelation: "",
  nomineeDob: "",
  nomineeAge: "",
  paCoverYears: 1,
  // UI-only (never sent): true once the PA term has been picked on purpose — or came from a saved policy. A National TP
  // asks the term in the TP panel with NO default, so the form's 1 is not a choice until this is true.
  _paYearsChosen: false,
  otherRelationName: "",
  appointeeName: "",
  // KSHEMA only. "Individual" matches the portal's own default — the far more
  // common case, and the one its screenshot ships pre-selected.
  nomineeType: "individual",
  // KSHEMA only. This form captures a SINGLE nominee, so the whole benefit
  // goes to them; 100 is the only value that validates without adding a
  // second nominee row. Left editable in case the portal ever splits it.
  nomineeShare: "100",
  hasFinancier: false,
  financierType: "",
  financierName: "",
  financierAddress: "",
  // The customer's existing TP policy (OD only: required; two insurers: optional) — CustomerDetailsStep
  existingTpPolicyNumber: "",
  existingTpInsurer: "",
  existingTpStartDate: "",
  existingTpEndDate: "",
  Paymentmethod: "wallet",
  notPaCoverReason: "",
};

export const customerDetailsInitialValues = {
  // Customer Details
  fullName: "",
  surname: "",
  // KSHEMA only (the switch on the Surname label): type the Surname into KSHEMA's Last Name box. Off = left empty.
  kshemaSendSurname: false,
  dateOfBirth: "",
  gender: "",
  // Reliance's Title (Mr. / Ms. / Mrs.). "" until chosen or derived from the Gender; the payload never sends "".
  proposerTitle: "",
  // UI-only (never sent): true once the operator picked the title (or it came from a saved policy), so a later Gender
  // change no longer overrides it.
  _titleChosen: false,
  // UI-only (never sent): what the Aadhaar scan said that bears on the title — a printed salutation (Shri / Smt ...)
  // and the guardian prefix (S/O, D/O, W/O). Kept so a later Gender change, or the other side being read afterwards,
  // still suggests the right title (utils/proposerTitle.js suggestTitle).
  _aadharSalutation: "",
  _aadharRelation: "",
  // UI-only (never sent): a freshly uploaded Aadhaar waits for the operator's "Read Aadhaar & fill details" (2026-11-05:
  // an upload is no longer sent to the AI by itself). Next stops while it is true on a new policy.
  _aadharToRead: false,
  fatherName: "",
  panCardFront: null,
  panCardBack: null,
  panCard: null, // Merged PAN card image (front + back)
  aadharCardFront: null,
  aadharCardBack: null,
  aadharCard: null, // Merged Aadhar card image (front + back)
  // Read off the uploaded Aadhaar by OCR — never typed. Shown read-only so the
  // operator can eyeball it against the card, and blocked at Next when the
  // scan was too poor to yield 12 digits.
  aadharNumber: "",

  // Address Details
  flatDoorNo: "",
  floorNo: "",
  buildingName: "",
  blockName: "",
  roadStreetLane: "",
  areaAndLocality: "",
  state: "tamilnadu",
  country: "India",
  pincode: "",
  email: "",
  mobileNumber: "",

  // Vehicle Details
  engineNumber: "",
  chassisNumber: "",
};

export const formSteps = [
  "Basic Policy Details",
  "Customer, Address & Vehicle Details",
  "Preview",
];
